import React, { Component } from 'react';
import { Form, Field } from 'react-final-form';
import {
  TextField,
  NumberField,
  ClassificatorField,
  SelectField,
} from '../../atoms';
import { getLoadingSelector } from '../../utils/asyncHelpers';
import { Row, Col, Button, Spin } from 'antd';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { FormActions } from '../../components';
import { FORM_CLASSIFIERS_GENERAL_EDIT } from '../../constants/formKeys';
import { getClassifierGeneralChange } from '../../reducers/classifiersReducer';
import * as actions from '../../actions/actionCreators/classifiersActionCreators';
import {
  classifierGeneralItemField,
  classifierGeneralItemLangField,
  classifierConsumptionUsageProperties,
  classifierGeneralItemPropertiesField,
  classifierProperties,
} from '../../constants/classifier';
import { goToClassifierSubView } from '../../utils/gotoLink';
import classificatorTypes from '../../constants/classificators';
import validateSchema from './validateSchema';
import classifiersStore from '../../utils/classifiersStore';
import FormStateToRedux from '../FormStateToRedux';
import { makeRffValidateFromLegacy } from '../rffLegacyValidateAdapter';

export class ClassifierGeneralEdit extends Component {
  static propTypes = {
    t: PropTypes.func.isRequired,
    onComplete: PropTypes.func.isRequired,
    onCancel: PropTypes.func.isRequired,
    isLoading: PropTypes.bool.isRequired,
    isEdit: PropTypes.bool.isRequired,
    classifierCode: PropTypes.string,
    initialValues: PropTypes.object,
    selectedCode: PropTypes.string,
    changeClassifierGeneral: PropTypes.func.isRequired,
    fetchClassifierGeneral: PropTypes.func.isRequired,
  };

  onSubmit = async (values) => {
    const {
      classifierCode,
      onComplete,
      changeClassifierGeneral,
      fetchClassifierGeneral,
    } = this.props;

    const oldProperties = values[classifierGeneralItemField.PROPERTIES] || {};
    const transformedValues = {
      ...values,
      [classifierGeneralItemField.PROPERTIES]: Object.keys(oldProperties).map(
        (key) => ({
          [classifierGeneralItemPropertiesField.CODE]: key,
          [classifierGeneralItemPropertiesField.VALUE]: oldProperties[key],
        })
      ),
    };

    await changeClassifierGeneral(classifierCode, transformedValues);
    fetchClassifierGeneral(classifierCode);
    onComplete();

    await classifiersStore.update();
  };

  renderAdditional = () => {
    const { t, classifierCode } = this.props;

    if (classifierCode === classificatorTypes.consumptionUsage) {
      return (
        <React.Fragment>
          <Row gutter={32}>
            <Col span={12}>
              <Field
                className="date-field"
                name={`${classifierGeneralItemField.PROPERTIES}.${
                  classifierConsumptionUsageProperties.START_DAY
                }`}
                label={t('fieldDateStart')}
                component={NumberField}
                min={1}
                max={31}
              />
            </Col>
            <Col span={12}>
              <Field
                className="date-field"
                name={`${classifierGeneralItemField.PROPERTIES}.${
                  classifierConsumptionUsageProperties.END_DAY
                }`}
                label={t('fieldDateEnd')}
                component={NumberField}
                min={1}
                max={31}
              />
            </Col>
          </Row>
          <Row gutter={32}>
            <Col span={12}>
              <Field
                className="date-field"
                name={`${classifierGeneralItemField.PROPERTIES}.${
                  classifierConsumptionUsageProperties.SUPPORT_REGULATION
                }`}
                label={t('fieldSupportRegulation')}
                component={NumberField}
                min={1}
                max={1000}
                isFloat
              />
            </Col>
            <Col span={12}>
              <Field
                className="date-field"
                name={`${classifierGeneralItemField.PROPERTIES}.${
                  classifierConsumptionUsageProperties.PERIOD
                }`}
                label={t('fieldPeriod')}
                component={ClassificatorField}
                classificatorType={
                  ClassificatorField.types.consumptionUsagePeriod
                }
              />
            </Col>
          </Row>
          <Row gutter={32}>
            <Col span={12}>
              <Field
                className="date-field"
                name={`${classifierGeneralItemField.PROPERTIES}.${
                  classifierConsumptionUsageProperties.SUBSIDY_IN_ENGLISH
                }`}
                label={t('fieldSubsidyInEnglish')}
                component={TextField}
              />
            </Col>
            <Col span={12}>
              <Field
                className="date-field"
                name={`${classifierGeneralItemField.PROPERTIES}.${
                  classifierConsumptionUsageProperties.SUBSIDY_IN_ESTONIAN
                }`}
                label={t('fieldSubsidyInEstonian')}
                component={TextField}
              />
            </Col>
          </Row>
        </React.Fragment>
      );
    }

    return null;
  };

  render() {
    const {
      t,
      onCancel,
      isLoading,
      isEdit,
      selectedCode,
      initialValues: initialValuesProp,
      classifierCode,
    } = this.props;

    // Convert Immutable objects to plain JS (parent passes Immutable Maps for lang/properties)
    const initialValues = initialValuesProp
      ? {
          ...initialValuesProp,
          [classifierGeneralItemField.LANG]: initialValuesProp[
            classifierGeneralItemField.LANG
          ]?.toJS
            ? initialValuesProp[classifierGeneralItemField.LANG].toJS()
            : initialValuesProp[classifierGeneralItemField.LANG] || {},
          [classifierGeneralItemField.PROPERTIES]: initialValuesProp[
            classifierGeneralItemField.PROPERTIES
          ]?.toJS
            ? initialValuesProp[classifierGeneralItemField.PROPERTIES].toJS()
            : initialValuesProp[classifierGeneralItemField.PROPERTIES] || {},
        }
      : {};

    const validate = makeRffValidateFromLegacy(
      validateSchema,
      this.props,
      initialValues
    );

    return (
      <Spin spinning={isLoading}>
        <Form
          onSubmit={this.onSubmit}
          initialValues={initialValues}
          initialValuesEqual={() => true}
          validate={validate}
          render={({ handleSubmit }) => (
            <form onSubmit={handleSubmit}>
              <FormStateToRedux form={FORM_CLASSIFIERS_GENERAL_EDIT} />
              <Row gutter={32}>
                <Col span={24}>
                  <Field
                    name={classifierGeneralItemField.CODE}
                    label={t('fieldCode')}
                    component={TextField}
                    disabled={isEdit}
                  />
                </Col>
              </Row>
              <Row gutter={32}>
                <Col span={12}>
                  <Field
                    name={`${classifierGeneralItemField.LANG}.${
                      classifierGeneralItemLangField.EN
                    }`}
                    label={t('fieldEn')}
                    component={TextField}
                  />
                </Col>
                <Col span={12}>
                  <Field
                    name={`${classifierGeneralItemField.LANG}.${
                      classifierGeneralItemLangField.ET
                    }`}
                    label={t('fieldEt')}
                    component={TextField}
                  />
                </Col>
              </Row>
              {
                // If classifier_value_property table have related records with code 'property' then show
                initialValues?.properties?.property && (
                  <Row gutter={32}>
                    <Col span={24}>
                      <Field
                        name={`${classifierGeneralItemField.PROPERTIES}.${
                          classifierProperties.PROPERTY
                        }`}
                        label={t('property')}
                        component={TextField}
                      />
                    </Col>
                  </Row>
                )
              }
              {
                // If classifier_value_property table have related records with code 'boolean' then show
                initialValues?.properties?.boolean && (
                  <Row gutter={32}>
                    <Col span={24}>
                      <Field
                        name={`${classifierGeneralItemField.PROPERTIES}.${classifierProperties.BOOLEAN}`}
                        label={t('boolean')}
                        component={SelectField}
                        options={[
                          { value: 'enabled', label: 'Enabled' },
                          {
                            value: 'disabled',
                            label: 'Disabled',
                          },
                        ]}
                      />
                    </Col>
                  </Row>
                )
              }
              {this.renderAdditional()}
              <FormActions>
                {selectedCode !== null && classifierCode === 'FUEL_TYPE' && (
                  <Button
                    onClick={() => {
                      onCancel();
                      goToClassifierSubView(classifierCode, initialValues.code);
                    }}
                  >
                    {t('buttonSubClassifiers')}
                  </Button>
                )}
                <Button onClick={onCancel}>{t('buttonCancel')}</Button>
                <Button htmlType="submit" type="primary">
                  {t('buttonSave')}
                </Button>
              </FormActions>
            </form>
          )}
        />
      </Spin>
    );
  }
}

const getIsLoading = getLoadingSelector(getClassifierGeneralChange);

const mapStateToProps = (state) => ({
  isLoading: getIsLoading(state),
});

const mapDispatchToProps = {
  changeClassifierGeneral: actions.changeClassifierGeneral,
  fetchClassifierGeneral: actions.fetchClassifierGeneral,
};

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(ClassifierGeneralEdit);
